import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Rocket, Ship, Plane, ArrowRight, CheckCircle2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCMS } from '../hooks/useCMS';
import { trackPageView } from '../lib/analytics';

// /live 分幕落地頁（live-funnel 組件）——FB 直播流量入口（連結帶 ?src=fblive 自動開 chat）
// 三幕：迎賓 → 痛點對比（雙標籤） → 限時 CTA。全部文案 CMS 可編（live.page.*）。

export default function LiveLanding() {
  const { getValue, getHtml } = useCMS();

  useEffect(() => {
    trackPageView('/live');
  }, []);

  const b2bRows = [
    { old: getValue('live.page.b2b.row1_old', '清關自己摸路，敏感貨易扣關'), neu: getValue('live.page.b2b.row1_new', '包稅專線一條龍，食品敏感貨可走') },
    { old: getValue('live.page.b2b.row2_old', '海外自建倉貴、出貨慢'), neu: getValue('live.page.b2b.row2_new', '海外一件代發，訂單直達') },
    { old: getValue('live.page.b2b.row3_old', '快遞／清關／倉儲逐家傾'), neu: getValue('live.page.b2b.row3_new', '一個窗口，全程搞掂') },
  ];
  const b2cRows = [
    { old: getValue('live.page.b2c.row1_old', '集貨等單，動輒幾個禮拜'), neu: getValue('live.page.b2c.row1_new', '專線直郵，快速穩定') },
    { old: getValue('live.page.b2c.row2_old', '代購來源不明，冇保證'), neu: getValue('live.page.b2c.row2_new', '香港門市直發，正貨保證') },
    { old: getValue('live.page.b2c.row3_old', '人手報價，各處不同'), neu: getValue('live.page.b2c.row3_new', '站上即時運費試算') },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* 幕一：迎賓 */}
      <section className="relative bg-x1493-dark text-white py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 140% at 15% 0%, #1a6b50 0%, #12503c 38%, #0b3527 100%)' }} />
        <div className="absolute inset-0 bg-x1493-dark/40" />
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <Rocket size={44} className="text-x1493-secondary mx-auto mb-4" />
          <h1 className="text-3xl md:text-5xl font-bold mb-4 leading-tight" dangerouslySetInnerHTML={getHtml('live.page.hero_title', '多謝留直播間！<br />香港好貨 × 出海方案，跟進喺呢度')} />
          <p className="text-white/80 max-w-xl mx-auto mb-8" dangerouslySetInnerHTML={getHtml('live.page.hero_sub', '唔使四圍搵——話我知你係邊一路，我即刻幫你對號入座。')} />
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#b2b" className="inline-flex items-center justify-center gap-2 bg-x1493-secondary text-x1493-dark font-bold px-7 py-3.5 rounded-2xl hover:brightness-105 transition">
              <Ship size={20} />
              {getValue('live.page.btn_b2b', '我想把貨賣出海外')}
            </a>
            <a href="#b2c" className="inline-flex items-center justify-center gap-2 border-2 border-white/80 text-white font-bold px-7 py-3.5 rounded-2xl hover:bg-white/10 transition">
              <Plane size={20} />
              {getValue('live.page.btn_b2c', '我想買香港貨')}
            </a>
          </div>
        </div>
      </section>

      {/* 幕二：痛點對比 */}
      <section className="py-14 bg-stone-50">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* B2B 卡 */}
          <div id="b2b" className="bg-white border border-stone-200 rounded-3xl p-6 scroll-mt-24">
            <div className="flex items-center gap-2 mb-4">
              <Ship size={22} className="text-x1493-primary" />
              <h2 className="font-bold text-lg">{getValue('live.page.b2b_title', '香港品牌出海')}</h2>
            </div>
            <p className="text-sm text-stone-500 mb-4">{getValue('live.page.b2b_pain', '貨好，但出海門檻高、環節多、無從入手？')}</p>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-stone-500 text-xs">
                  <th className="pb-2 pr-2">傳統出海</th>
                  <th className="pb-2 text-x1493-primary">YYC 一站式</th>
                </tr>
              </thead>
              <tbody className="align-top">
                {b2bRows.map((r, i) => (
                  <tr key={i} className="border-t border-stone-100">
                    <td className="py-2 pr-2 text-stone-500">{r.old}</td>
                    <td className="py-2 font-bold">{r.neu}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* B2C 卡 */}
          <div id="b2c" className="bg-white border border-stone-200 rounded-3xl p-6 scroll-mt-24">
            <div className="flex items-center gap-2 mb-4">
              <Plane size={22} className="text-x1493-primary" />
              <h2 className="font-bold text-lg">{getValue('live.page.b2c_title', '海外港人買港貨')}</h2>
            </div>
            <p className="text-sm text-stone-500 mb-4">{getValue('live.page.b2c_pain', '想食返口香港味道，又怕慢、怕假貨？')}</p>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-stone-500 text-xs">
                  <th className="pb-2 pr-2">一般代購集運</th>
                  <th className="pb-2 text-x1493-primary">YYC 官方直郵</th>
                </tr>
              </thead>
              <tbody className="align-top">
                {b2cRows.map((r, i) => (
                  <tr key={i} className="border-t border-stone-100">
                    <td className="py-2 pr-2 text-stone-500">{r.old}</td>
                    <td className="py-2 font-bold">{r.neu}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 幕三：限時 CTA */}
      <section className="py-14">
        <div className="max-w-4xl mx-auto px-4">
          <div className="rounded-3xl p-8 md:p-10 text-center text-white" style={{ background: 'linear-gradient(120deg, #0b3527 0%, #12503c 60%, #1a6b50 100%)' }}>
            <h2 className="text-2xl md:text-3xl font-bold mb-3" dangerouslySetInnerHTML={getHtml('live.page.cta_title', '直播觀眾專屬 · 限時開放')} />
            <ul className="text-sm text-white/85 space-y-1.5 mb-8 max-w-md mx-auto text-left">
              <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-x1493-secondary mt-0.5 shrink-0" />{getValue('live.page.cta_line1', 'B2B：免費出海方案診斷——顧問一對一計清航線、清關、代發成本')}</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-x1493-secondary mt-0.5 shrink-0" />{getValue('live.page.cta_line2', 'B2C：直播限定優惠即刻領、即刻結帳，YYC 直郵到你門口')}</li>
            </ul>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/b2b" className="inline-flex items-center justify-center gap-2 bg-x1493-secondary text-x1493-dark font-bold px-7 py-3.5 rounded-2xl hover:brightness-105 transition">
                {getValue('live.cta.b2b', '預約免費出海方案診斷')}
                <ArrowRight size={18} />
              </Link>
              <Link to="/offers" className="inline-flex items-center justify-center gap-2 bg-white text-x1493-primary font-bold px-7 py-3.5 rounded-2xl hover:bg-stone-100 transition">
                {getValue('live.cta.b2c', '領取直播限定優惠並結帳')}
                <ArrowRight size={18} />
              </Link>
            </div>
            <p className="text-xs text-white/60 mt-5">{getValue('live.page.cta_note', '* 優惠及診斷名額以直播公佈為準，本公司保留最終決定權')}</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
